import { PROBE_GROUPS, get_probe_definition } from "@shared/probe_manifest";
import type { JsonValue, ProbeReport, ProbeReportStatus } from "@shared/probe_types";

export type SupportState = "supported" | "unknown" | "unsupported";

export type SupportSummaryTone = "danger" | "positive" | "warning";

export interface SupportSummary {
  readonly label: string;
  readonly ratio_label: string;
  readonly supported_count: number;
  readonly tone: SupportSummaryTone;
  readonly total_count: number;
}

export interface ProbeGroupSummary {
  readonly group_title: string;
  readonly supported_count: number;
  readonly total_count: number;
  readonly unknown_count: number;
  readonly unsupported_count: number;
}

export interface ProbeObservationRow {
  readonly detail_json: string;
  readonly detail_summary: string;
  readonly duration_ms: number;
  readonly error_message: string | null;
  readonly group_title: string;
  readonly implementation_kind: string;
  readonly probe_id: string;
  readonly requires_cross_origin_isolation: boolean;
  readonly summary: string;
  readonly support_state: SupportState;
  readonly title: string;
}

export interface ProbeReportSummary {
  readonly average_duration_ms: number;
  readonly browser_label: string;
  readonly context_kind: string;
  readonly cross_origin_isolated: boolean;
  readonly error_count: number;
  readonly headline: string;
  readonly is_secure_context: boolean;
  readonly probe_id: string;
  readonly profile: string;
  readonly report_status: ProbeReportStatus;
  readonly source: string;
  readonly submitted_at: string;
  readonly supported_count: number;
  readonly total_duration_ms: number;
  readonly total_observation_count: number;
  readonly unknown_count: number;
  readonly unsupported_count: number;
  readonly user_agent: string;
}

export interface ProbeReportView {
  readonly group_summaries: readonly ProbeGroupSummary[];
  readonly observation_rows: readonly ProbeObservationRow[];
  readonly raw_report_json: string;
  readonly summary: ProbeReportSummary;
}

interface MutableGroupSummary {
  group_title: string;
  order: number;
  supported_count: number;
  total_count: number;
  unknown_count: number;
  unsupported_count: number;
}

const DEFAULT_GROUP_TITLE = "Unmapped";
const DEFAULT_PROFILE = "full";
const PROBE_GROUP_ORDER = new Map(
  PROBE_GROUPS.map(
    (group, index): readonly [string, number] => [group.id, index]
  )
);
const PROBE_GROUPS_BY_ID = new Map(
  PROBE_GROUPS.map((group): readonly [string, string] => [group.id, group.title])
);

export function build_probe_report_view(report: ProbeReport): ProbeReportView {
  const observation_rows = report.observations.map(
    (observation): ProbeObservationRow => {
      const probe_definition = get_probe_definition(observation.id);
      const group_title =
        probe_definition === undefined
          ? DEFAULT_GROUP_TITLE
          : read_group_title(probe_definition.group_id);

      return {
        probe_id: observation.id,
        title: probe_definition?.title ?? observation.id,
        group_title,
        support_state: read_support_state(observation.supported),
        duration_ms: observation.duration_ms,
        implementation_kind: probe_definition?.implementation_kind ?? "unknown",
        requires_cross_origin_isolation:
          probe_definition?.requires_cross_origin_isolation ?? false,
        summary:
          probe_definition?.summary ?? "Probe definition metadata is unavailable.",
        detail_summary: summarize_json_value(observation.detail),
        detail_json: JSON.stringify(observation.detail, null, 2),
        error_message: observation.error_message
      };
    }
  );
  const supported_count = observation_rows.filter(
    (row): boolean => row.support_state === "supported"
  ).length;
  const unsupported_count = observation_rows.filter(
    (row): boolean => row.support_state === "unsupported"
  ).length;
  const unknown_count = observation_rows.filter(
    (row): boolean => row.support_state === "unknown"
  ).length;
  const error_count = observation_rows.filter(
    (row): boolean => row.error_message !== null
  ).length;
  const total_duration_ms = round_duration(
    observation_rows.reduce(
      (duration_sum, row): number => duration_sum + row.duration_ms,
      0
    )
  );
  const average_duration_ms = round_duration(
    observation_rows.length === 0 ? 0 : total_duration_ms / observation_rows.length
  );

  return {
    observation_rows,
    group_summaries: build_group_summaries(observation_rows),
    raw_report_json: JSON.stringify(report, null, 2),
    summary: {
      probe_id: report.probe_id,
      report_status: report.status,
      submitted_at: report.submitted_at,
      source: report.target.source,
      profile: read_profile(report.runtime.href),
      browser_label:
        report.target.browser_key ?? simplify_user_agent(report.runtime.user_agent),
      user_agent: report.runtime.user_agent,
      context_kind: report.runtime.context_kind,
      is_secure_context: report.runtime.is_secure_context,
      cross_origin_isolated: report.runtime.cross_origin_isolated,
      total_observation_count: observation_rows.length,
      supported_count,
      unsupported_count,
      unknown_count,
      error_count,
      total_duration_ms,
      average_duration_ms,
      headline: build_headline(
        observation_rows.length,
        supported_count,
        unsupported_count,
        unknown_count,
        error_count
      )
    }
  };
}

export function read_support_state(
  supported: boolean | null
): ProbeObservationRow["support_state"] {
  switch (supported) {
    case true:
      return "supported";
    case false:
      return "unsupported";
    case null:
      return "unknown";
  }
}

export function compare_group_title(left: string, right: string): number {
  const left_order = read_group_order(left);
  const right_order = read_group_order(right);

  if (left_order !== right_order) {
    return left_order - right_order;
  }

  return left.localeCompare(right);
}

export function summarize_support_states(
  support_states: readonly SupportState[]
): SupportSummary {
  const total_count = support_states.length;
  const supported_count = support_states.filter(
    (support_state): boolean => support_state === "supported"
  ).length;
  const error_count = support_states.filter(
    (support_state): boolean => support_state === "unknown"
  ).length;

  if (supported_count === total_count) {
    return {
      label: "Full Support",
      ratio_label: `${supported_count}/${total_count}`,
      supported_count,
      total_count,
      tone: "positive"
    };
  }

  if (supported_count > 0) {
    return {
      label: "Partial Support",
      ratio_label: `${supported_count}/${total_count}`,
      supported_count,
      total_count,
      tone: "warning"
    };
  }

  if (error_count > 0) {
    return {
      label: "Error",
      ratio_label: `${supported_count}/${total_count}`,
      supported_count,
      total_count,
      tone: "danger"
    };
  }

  return {
    label: "Not Supported",
    ratio_label: `${supported_count}/${total_count}`,
    supported_count,
    total_count,
    tone: "danger"
  };
}

function build_group_summaries(
  observation_rows: readonly ProbeObservationRow[]
): readonly ProbeGroupSummary[] {
  const group_summaries = new Map<string, MutableGroupSummary>();

  for (const row of observation_rows) {
    const group_summary = group_summaries.get(row.group_title) ?? {
      group_title: row.group_title,
      order: read_group_order(row.group_title),
      total_count: 0,
      supported_count: 0,
      unsupported_count: 0,
      unknown_count: 0
    };
    group_summary.total_count += 1;

    switch (row.support_state) {
      case "supported":
        group_summary.supported_count += 1;
        break;
      case "unsupported":
        group_summary.unsupported_count += 1;
        break;
      case "unknown":
        group_summary.unknown_count += 1;
        break;
    }

    group_summaries.set(row.group_title, group_summary);
  }

  return [...group_summaries.values()]
    .sort((left, right): number => {
      return compare_group_title(left.group_title, right.group_title);
    })
    .map(
      (group_summary): ProbeGroupSummary => ({
        group_title: group_summary.group_title,
        total_count: group_summary.total_count,
        supported_count: group_summary.supported_count,
        unsupported_count: group_summary.unsupported_count,
        unknown_count: group_summary.unknown_count
      })
    );
}

function build_headline(
  total_count: number,
  supported_count: number,
  unsupported_count: number,
  unknown_count: number,
  error_count: number
): string {
  const headline_parts = [
    `${supported_count} of ${total_count} probes reported support`,
    `${unsupported_count} reported unsupported`,
    `${unknown_count} came back unknown`
  ];

  if (error_count > 0) {
    headline_parts.push(`${error_count} returned an explicit error`);
  }

  return `${headline_parts.join(", ")}.`;
}

function read_group_order(group_title: string): number {
  for (const [group_id, title] of PROBE_GROUPS_BY_ID.entries()) {
    if (title === group_title) {
      return PROBE_GROUP_ORDER.get(group_id) ?? Number.POSITIVE_INFINITY;
    }
  }

  return Number.POSITIVE_INFINITY;
}

function read_group_title(group_id: string): string {
  return PROBE_GROUPS_BY_ID.get(group_id) ?? DEFAULT_GROUP_TITLE;
}

function read_profile(href: string): string {
  try {
    const current_url = new URL(href);
    const profile = current_url.searchParams.get("profile");
    return profile === null || profile.length === 0 ? DEFAULT_PROFILE : profile;
  } catch {
    return DEFAULT_PROFILE;
  }
}

function round_duration(duration_ms: number): number {
  return Math.round(duration_ms * 100) / 100;
}

function simplify_user_agent(user_agent: string): string {
  const browser_patterns = [
    /HeadlessChrome\/[^\s)]+/,
    /Chrome\/[^\s)]+/,
    /Firefox\/[^\s)]+/,
    /Safari\/[^\s)]+/
  ];

  for (const browser_pattern of browser_patterns) {
    const browser_match = browser_pattern.exec(user_agent);
    if (browser_match !== null) {
      return browser_match[0];
    }
  }

  return truncate_text(user_agent, 56);
}

function summarize_json_value(value: JsonValue): string {
  if (
    typeof value === "boolean" ||
    typeof value === "number" ||
    typeof value === "string" ||
    value === null
  ) {
    return String(value);
  }

  return truncate_text(JSON.stringify(value), 180);
}

function truncate_text(value: string, max_length: number): string {
  if (value.length <= max_length) {
    return value;
  }

  return `${value.slice(0, max_length - 3)}...`;
}
